import { useEffect, useRef, useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';

import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { cn } from '../../lib/utils';
import { formatSlashDate, formatDateTime } from '../../utils/dateUtils';
import { emptyDraft, recordToForm, formToPayload } from '../../utils/medicalRecordsMapping';
import {
  fetchMedicalRecords,
  createMedicalRecords,
  updateMedicalRecords,
} from '../../services/medicalRecordsService';

// Request-lifecycle rows, top to bottom. `flag` is the number of dots filled
// when a row's flag is active (mirrors the column's impact dots); rows without
// a flag render an empty Flag cell. The read-only Publish Date row instead
// shows the "Flag" column heading.
const REQUEST_ROWS = [
  { key: 'publishDt', label: 'Publish Date', readOnly: true, heading: true },
  { key: 'initialRequest', label: 'Initial Request', flag: 1 },
  { key: 'secondRequest', label: '2nd Request', flag: 2 },
  { key: 'thirdRequest', label: '3rd Request', flag: 3 },
  { key: 'recordsReceived', label: 'Records Received' },
  { key: 'recordsReviewed', label: 'Records Reviewed' },
];

const SMARTLIGHT_ROWS = [
  { key: 'slRequest', label: 'SmartLight Request', flag: 1 },
  { key: 'slReceived', label: 'SmartLight Received', flag: 2 },
  { key: 'slReviewed', label: 'SmartLight Reviewed', flag: 3 },
];

const DASH = '—';

// Display name for an audit user (the _AuditUserSerializer shape).
function userDisplayName(user) {
  if (!user) return '';
  const full = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return full || user.username || user.email || '';
}

function FlagDots({ filled, color, active, onToggle, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onToggle}
      className="inline-flex items-center justify-center gap-1 p-1 -m-1 justify-self-center pl-1"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={cn(
            'h-2 w-2 rounded-full transition-colors',
            active && i < filled ? color : 'bg-muted-foreground/30'
          )}
        />
      ))}
    </button>
  );
}

// Right-aligned label / date-input / flag triplet shared by both grids.
function Row({ label, children, flagCell }) {
  return (
    <div className="contents">
      <span className="text-xs font-semibold text-right whitespace-nowrap inline-flex items-center gap-1.5 justify-end">
        {label}
      </span>
      {children}
      {flagCell ?? <div />}
    </div>
  );
}

// Left panel of the MedicalRecordsDrawer: the request-status form. Loads the
// referral's medical-records record on open, pre-populates the fields, and
// persists edits back to the App DB. POST creates the first record; PUT
// updates an existing one. `onSaved` lets the parent refresh after a write.
function MedicalRecordsRequestStatus({ referral, open = true, onCancel, onSaved }) {
  const toast = useToast();
  const legacyReferralId = referral?.referral_id ?? null;

  const [draft, setDraft] = useState(emptyDraft);
  // Bookkeeping carried across saves so attempts update in place rather than
  // churn: the row id backing each slot, plus any attempts beyond the slots.
  const [attemptIds, setAttemptIds] = useState([null, null, null]);
  const [extraAttempts, setExtraAttempts] = useState([]);
  // The loaded record (null = empty state -> POST on submit) and the page
  // header context (publish date) from the GET payload.
  const [record, setRecord] = useState(null);
  const [referralContext, setReferralContext] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Flag toggles default to "on when the row has a date"; an explicit click
  // overrides that per row. Visual-only for now (flag auto-set is a separate
  // story).
  const [flagOverrides, setFlagOverrides] = useState({});

  // Snapshot of the last persisted draft; Submit is enabled only when the
  // current draft differs from it.
  const savedSnapshot = useRef(JSON.stringify(emptyDraft()));

  const seedFromRecord = (loadedRecord) => {
    const { draft: nextDraft, attemptIds: ids, extraAttempts: extras } =
      recordToForm(loadedRecord);
    setDraft(nextDraft);
    setAttemptIds(ids);
    setExtraAttempts(extras);
    setRecord(loadedRecord);
    setFlagOverrides({});
    savedSnapshot.current = JSON.stringify(nextDraft);
  };

  useEffect(() => {
    if (!open || !legacyReferralId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    fetchMedicalRecords(legacyReferralId)
      .then((payload) => {
        if (cancelled) return;
        setReferralContext(payload.referral_context ?? null);
        seedFromRecord(payload.medical_records ?? null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // legacyReferralId + open fully key the fetch; seedFromRecord is stable
    // enough for this one-shot load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, legacyReferralId]);

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const toggleFlag = (key, active) =>
    setFlagOverrides((prev) => ({ ...prev, [key]: !active }));

  const hasChanges = JSON.stringify(draft) !== savedSnapshot.current;

  const publishSource = referralContext?.publish_date ?? referral?.publish_dt;
  const publishDisplay = publishSource ? formatSlashDate(publishSource) : '';
  const updatedDateDisplay = record?.last_edited_at
    ? formatDateTime(record.last_edited_at)
    : '';
  const updatedByDisplay = userDisplayName(record?.last_edited_by);

  const handleSubmit = async () => {
    if (!legacyReferralId || saving) return;
    const payload = formToPayload(draft, attemptIds, extraAttempts);
    setSaving(true);
    try {
      const saved = record
        ? await updateMedicalRecords(legacyReferralId, payload)
        : await createMedicalRecords(legacyReferralId, payload);
      seedFromRecord(saved);
      toast.success('Medical records request saved.');
      onSaved?.(saved);
    } catch (err) {
      toast.error(`Failed to save medical records request: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderDateCell = (key, readOnly) => {
    if (readOnly) {
      return (
        <div className="h-7 w-[160px] text-xs flex items-center px-2 text-foreground">
          {publishDisplay || DASH}
        </div>
      );
    }
    return (
      <Input
        type="date"
        data-testid={`mr-date-${key}`}
        value={draft[key] ?? ''}
        disabled={loading || saving}
        onChange={(e) => setField(key, e.target.value)}
        className={cn('h-7 w-[160px] text-xs bg-background', !draft[key] && 'text-muted-foreground')}
      />
    );
  };

  const renderFlag = (key, filled, color) => {
    const active = flagOverrides[key] ?? !!draft[key];
    return (
      <FlagDots
        filled={filled}
        color={color}
        active={active}
        onToggle={() => toggleFlag(key, active)}
        label={`Toggle ${key} flag`}
      />
    );
  };

  return (
    <div className="overflow-auto pl-6 py-6 flex flex-col gap-2.5">
      <div className="flex items-center gap-2 min-h-[28px]">
        <ClipboardList className="h-4 w-4 text-primary" />
        <span className="text-sm font-bold tracking-tight text-foreground">
          Medical Records Request Status
        </span>
        {loading && (
          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" aria-label="Loading" />
        )}
      </div>

      {loadError ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-xs text-destructive"
        >
          Could not load the medical records request. {loadError.message}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
          {/* Carrier request lifecycle. Blue flag dots. */}
          <div className="grid grid-cols-[11rem_160px_auto] gap-x-3 gap-y-2 items-center pr-3">
            {REQUEST_ROWS.map((row) => (
              <Row
                key={row.key}
                label={row.label}
                flagCell={
                  row.heading ? (
                    <span className="text-xs font-semibold text-foreground justify-self-center pl-1">Flag</span>
                  ) : row.flag ? (
                    renderFlag(row.key, row.flag, 'bg-blue-500')
                  ) : undefined
                }
              >
                {renderDateCell(row.key, row.readOnly)}
              </Row>
            ))}
          </div>

          {/* SmartLight lifecycle + read-only audit fields. Black flag dots. */}
          <div className="grid grid-cols-[11rem_160px_auto] gap-x-3 gap-y-2 items-center mt-5 pr-3">
            {SMARTLIGHT_ROWS.map((row) => (
              <Row
                key={row.key}
                label={row.label}
                flagCell={renderFlag(row.key, row.flag, 'bg-black')}
              >
                {renderDateCell(row.key, false)}
              </Row>
            ))}
            <Row label="Updated Date">
              <div className="h-7 w-[160px] text-xs flex items-center px-2 text-foreground">
                {updatedDateDisplay || DASH}
              </div>
            </Row>
            <Row label="Updated By">
              <div className="h-7 w-[160px] text-xs flex items-center px-2 text-foreground">
                {updatedByDisplay || DASH}
              </div>
            </Row>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" disabled={!hasChanges || loading || saving} onClick={handleSubmit}>
          {saving ? 'Saving…' : 'Submit'}
        </Button>
      </div>
    </div>
  );
}

export { MedicalRecordsRequestStatus };
